import React, { useState, useEffect } from 'react'
import _camelCase from 'lodash/camelCase'
import PropTypes from 'prop-types'
import DOMPurify from 'dompurify'
import * as StringFormatter from '../../../global/formatters/string-formatter.js'
import { getRelativeBasePath } from '../../../global/environments/path.js'
import { getLinkFromRef, getLink, isPdfUrl, colorMap } from '../../utility'
import { Link2 } from '../link/link.jsx'

import {
  getAPIProductName,
  apiDataManipulation,
  getProductLink,
  productTypes,
} from './Helper.js'

export const ProductListing = ({ page, component }) => {
  const [parameters, setParameters] = useState({
    spacing: null,
    productType: null,
    backgroundColor: null,
  })
  const [blurb, setBlurb] = useState({
    id: null,
    document: null,
    title: null,
    subtitle: null,
    subtitle2: null,
    html: null,
    link: null,
    listType: null,
  })

  const [funds, setFunds] = useState([])
  const [categories, setCategories] = useState([])
  const fetchList = async () => {
    const productName = getAPIProductName(parameters?.productType)
    if (!productName) {
      return
    }
    const data = await apiDataManipulation(parameters?.productType, productName)
    setCategories(data?.categories)
    setFunds(data?.funds)
  }

  useEffect(() => {
    fetchList()
  }, [parameters])

  useEffect(() => {
    const _parameters = component.getParameters()
    const _models = component.getModels()
    const _document = _models?.document && page.getContent(_models?.document)
    setParameters({
      spacing: _parameters?.spacing,
      productType: _parameters?.productType,
      backgroundColor:
        colorMap[_parameters.backgroundColor || 'None']?.backgroundColor,
    })

    if (_document) {
      const data = _document?.getData() || {}
      setBlurb({
        id: _camelCase(data?.title, ' ', '-'),
        title: data?.title,
        subtitle: data?.subtitle,
        subtitle2: data?.subtitle2,
        html:
          data?.html && typeof data?.html === 'object'
            ? data.html.value
            : data.html || data.content,
        link: getLink(getLinkFromRef(data?.linkCompound, page), null),
        listType: data?.linkList?.tableHeader,
        linkList: data?.linkList?.links
          .filter((l) => l.link && l.link.length > 0)
          .map((l) => getLink(getLinkFromRef(l, page), null)),
      })
    }
  }, [])

  const onChangeFundDropdown = (event) => {
    location.href = event.currentTarget.value
  }

  const productUrl = `${getRelativeBasePath()}/${blurb?.link?.url}`

  return (
    <>
      {parameters?.productType === productTypes.MF && (
        <div id="is-firm-specific-content-zone-9"></div>
      )}

      <div
        className={`u-paddingVert12gu ${parameters.backgroundColor} ${parameters?.spacing}`}
      >
        <div className="u-sizeConstrained">
          <article
            id={blurb?.id}
            data-qa-automation={`${blurb?.id}-section`}
            className="u-flex u-sm-flexCol"
            role="article"
          >
            <div className="u-md-size6of12 u-lg-size6of12">
              <a
                className="Link u-textColorDefault"
                href={DOMPurify.sanitize(productUrl)}
                data-qa-automation={`${blurb?.id}-title`}
              >
                <h3>{blurb?.title}</h3>
              </a>
              <div
                className="u-paddingTop2gu u-paddingBottom4gu u-paddingRight4gu u-sm-paddingRight0"
                dangerouslySetInnerHTML={{
                  __html: DOMPurify.sanitize(blurb?.html),
                }}
              />
              <Link2
                url={productUrl}
                text={blurb?.link?.text}
                arrow="brand"
                className={`Link u-posRelative hover-underline-animation`}
                textClasses={`u-textSecondary u-textKernLoose`}
                data-qa-automation={`${blurb?.id}-link`}
              />
            </div>
            <div className="u-md-size6of12 u-lg-size6of12 u-sm-marginTop4gu u-paddingLeft4gu u-sm-paddingLeft0">
              {funds?.length > 0 && (
                <div className="Dropdown u-sizeFull u-marginBottom4gu">
                  <label
                    className="u-hiddenVisually"
                    htmlFor={`${blurb?.id}-finder-dropdown`}
                  >
                    {blurb?.subtitle}
                  </label>
                  <select
                    id={`${blurb?.id}-finder-dropdown`}
                    data-qa-automation={`${blurb?.id}-finder-dropdown`}
                    className="Dropdown-menu u-paddingVert2gu"
                    onChange={onChangeFundDropdown}
                  >
                    <option key="blank" value="">
                      Select a {blurb?.subtitle}
                    </option>
                    {funds.map((fund) => {
                      return (
                        <option
                          key={fund.fundName}
                          value={`${productUrl}/${StringFormatter.urlSafe(
                            fund.fundName
                          )}/Class-${fund.fundClass}/details/?cusip=${
                            fund.cusip
                          }`}
                        >
                          {parameters?.productType === productTypes.ETF
                            ? `${fund.symbol} -`
                            : ''}{' '}
                          {fund.fundName}
                        </option>
                      )
                    })}
                  </select>
                </div>
              )}
              <ul className="List">
                {blurb?.linkList?.map((itm, idx) => {
                  const lh = blurb?.linkList?.length
                  return (
                    <li
                      key={`${itm?.text}${idx}`}
                      className={`u-borderTop u-borderColorNeutralLight u-paddingVert4gu 
                      ${lh !== 1 && idx === lh - 1 ? 'u-borderBottom' : ''}  
                    `}
                    >
                      <a
                        className="Link"
                        href={DOMPurify.sanitize(
                          getProductLink(productUrl, itm?.url)
                        )}
                        target={isPdfUrl(itm?.url) ? '_blank' : '_self'}
                        rel="noreferrer"
                        data-qa-automation={`${blurb?.id}-links-${idx}`}
                      >
                        {itm?.text}
                      </a>
                    </li>
                  )
                })}
                {categories.map((fundCategory, idx) => (
                  <li
                    key={fundCategory.url}
                    className="u-borderColorNeutralLight u-borderBottom u-paddingVert4gu"
                    data-category={fundCategory.url}
                  >
                    <a
                      className="Link"
                      href={DOMPurify.sanitize(
                        `${productUrl}?investment-category=${fundCategory.url}`
                      )}
                      title={
                        fundCategory.name + ' (' + fundCategory.count + ')'
                      }
                      data-qa-automation={`${blurb?.id}-categories-${idx}`}
                    >
                      {fundCategory.name} ({fundCategory.count})
                    </a>
                  </li>
                ))}
              </ul>
              {blurb?.subtitle2 && (
                <p className="u-borderBottom u-borderColorNeutralLight u-paddingBottom4gu">
                  {blurb?.subtitle2}
                </p>
              )}
            </div>
          </article>
        </div>
      </div>
    </>
  )
}

ProductListing.propTypes = {
  page: PropTypes.object,
  component: PropTypes.object,
}
